import Container from 'typedi'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import type { SecurityContext } from '../models/security-context.model'
import type { ResetPassword } from '../models/reset-password.model'
import type { Register } from '../models/register.model'
import type { ChangePassword } from '../models/change-password.model'
import type { ApiCredentials } from '../models/api-credentials.model'
import SecurityService from '../services/security.service'
import GtrStorage from '../services/storage.service'
import FileUploadService from '@/modules/common/services/file-upload.service'
import StaxBillService from '@/modules/common/services/staxbill.service'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import { AccessLevel } from '@/modules/common/enums/access-levels.enum'

@Module({
  namespaced: true
})
export default class SecurityStore extends VuexModule {
  securityContext: SecurityContext | undefined = undefined
  activationStatus: boolean | undefined = undefined
  twofactorSent: boolean | undefined = false
  currentUser: any | undefined = undefined
  paymentMethods: any[] = []
  user: Record<string, any> | null = null
  zapierKeys: any = []

  get getSecurityContext (): SecurityContext | undefined {
    return this.securityContext
  }

  get isSuperAdmin () {
    const user = (this as any).currentUser
    if (user) {
      return user.access_level === AccessLevel.SUPER_ADMIN || user.access_level === AccessLevel.DEVELOPER
    }
    return false
  }

  get isLMSAdmin () {
    const user = (this as any).currentUser
    if (user) {
      return user.access_level === 'LMS_ADMIN'
    }
    return false
  }

  @Mutation
  SET_ZAPIER_KEYS (payload: any) {
    if (payload) {
      this.zapierKeys = payload
    } else {
      this.zapierKeys = []
    }
  }

  @Mutation
  SET_SECURITY_CONTEXT (securityContext: SecurityContext | undefined) {
    this.securityContext = securityContext
  }

  @Mutation
  SET_ACTIVATION_STATUS (status: boolean) {
    this.activationStatus = status
  }

  @Mutation
  SET_CURRENT_USER (payload: any) {
    this.currentUser = payload
  }

  @Mutation
  SET_PAYMENT_METHODS (payload: any[]) {
    this.paymentMethods = payload
  }

  @Mutation
  UPDATE_PAYMENT_METHOD ({ newCard, originalId }: any) {
    if (originalId) {
      const index = this.paymentMethods.findIndex((pm) => pm.stax_bill_payment_method_id === originalId)
      this.paymentMethods[index] = {
        ...newCard
      }
    } else {
      this.paymentMethods.push(newCard)
    }
  }

  @Mutation
  SET_USER (payload: any) {
    this.user = payload
  }

  @Mutation
  SET_TWO_FACTOR_SENT (payload: any) {
    this.twofactorSent = payload
  }

  @Action
  setSecurityContext (paylaod: SecurityContext) {
    this.context.commit('SET_SECURITY_CONTEXT', paylaod)
  }

  @Action
  setSecurityContextStorage (payload: any) {
    const context: any = {
      access_token: payload.access_token,
      access_level: payload.user?.access_level,
      expires_in: payload.expires_in,
      expires_at: payload.expires_at,
      user: payload.user,
      company: payload.user?.company,
      has_company: payload.user?.company !== null
    }
    if (payload.impersonator) {
      context.impersonator = payload.impersonator
    }
    Container.get(GtrStorage).setItem('security_context', JSON.stringify(context))
  }

  @Action({ rawError: true })
  async login (payload: ApiCredentials) {
    const response: any = await Container.get(SecurityService).login(payload)
    if (response.data.authorization_type === '2fa') {
      const _payload = {
        data: {
          email: payload.email,
          password: payload.password
        }
      }
      this.context.dispatch('get2faCode', _payload)
    } else {
      this.context.commit('SET_SECURITY_CONTEXT', response.data)
      this.context.dispatch('setSecurityContextStorage', response.data)
      if (response.data.remember_2fa_token) {
        Container.get(GtrStorage).setItem('remember_2fa_token', response.data.remember_2fa_token)
      }
      if (response.data.device_token) {
        Container.get(GtrStorage).setItem('device_token', response.data.device_token)
      }
    }
    return response.data
  }

  @Action({ rawError: true })
  async refreshToken () {
    const response: any = await Container.get(SecurityService).refreshToken()
    this.context.commit('SET_SECURITY_CONTEXT', response.data)
    this.context.dispatch('setSecurityContextStorage', response.data)
    return response.data
  }

  @Action({ rawError: true })
  async register (payload: Register) {
    const response = await Container.get(SecurityService).register(payload)
    return response
  }

  @Action({ rawError: true })
  async activateAccount (payload: string) {
    const response: any = await Container.get(SecurityService).activateAccount(
      payload
    )
    if (response.data.user.uuid) {
      this.context.commit('SET_ACTIVATION_STATUS', true)
    } else {
      this.context.commit('SET_ACTIVATION_STATUS', false)
    }
    return response
  }

  @Action({ rawError: true })
  async logout () {
    await Container.get(SecurityService).logout()
    Container.get(GtrStorage).removeItem('current_2fa_token')
    Container.get(GtrStorage).removeItem('security_context')
  }

  @Action({ rawError: true })
  async resetPassword (payload: ResetPassword) {
    await Container.get(SecurityService).resetPassword(payload)
  }

  @Action({ rawError: true })
  async changePassword (payload: ChangePassword) {
    await Container.get(SecurityService).changePassword(payload)
  }

  @Action({ rawError: true })
  async usePasswordToken (token: string) {
    const response = await Container.get(SecurityService).usePasswordToken(token)
    return response
  }

  @Action({ rawError: true })
  async generateZapierKey (payload) {
    const response = await Container.get(SecurityService).generateZapierKey(payload)
    await this.context.dispatch('getZapierKeys')
    return response
  }

  @Action({ rawError: true })
  async updateZapierKey (payload) {
    const response = await Container.get(SecurityService).updateZapierKey(payload)
    await this.context.dispatch('getZapierKeys')
    return response
  }

  @Action({ rawError: true })
  async getZapierKeys () {
    const response = await Container.get(SecurityService).getZapierKeys()
    this.context.commit('SET_ZAPIER_KEYS', response.data)
    return response
  }

  @Action({ rawError: true })
  async stopImpersonating () {
    const response = await Container.get(SecurityService).stopImpersonating()
    this.context.commit('SET_SECURITY_CONTEXT', response.data)
    this.context.dispatch('setSecurityContextStorage', response.data)
    return response
  }

  @Action({ rawError: true })
  async impersonateUser (user_uuid: string) {
    const response = await Container.get(SecurityService).impersonateUser(user_uuid)
    this.context.commit('SET_SECURITY_CONTEXT', response.data)
    this.context.dispatch('setSecurityContextStorage', response.data)
    return response
  }

  @Action({ rawError: true })
  async getCurrentUser () {
    const response = await Container.get(SecurityService).getCurrentUser()
    this.context.commit('SET_CURRENT_USER', response.data)
    return response
  }

  @Action({ rawError: true })
  async getPaymentMethods () {
    const response = await Container.get(SecurityService).getPaymentMethods()
    this.context.commit('SET_PAYMENT_METHODS', response.data)
    return response
  }

  @Action({ rawError: true })
  async deleteAccess (payload: any) {
    const { user_uuid, event_uuid } = payload
    const response = await Container.get(SecurityService).deleteAccess(user_uuid, event_uuid)
    this.context.dispatch('getUser', payload)
    return response
  }

  @Action({ rawError: true })
  async addAccess (payload: any) {
    const { user_uuid, event_uuid } = payload
    const response = await Container.get(SecurityService).addAccess(user_uuid, event_uuid)
    this.context.dispatch('getUser', payload)
    return response
  }

  @Action({ rawError: true })
  async getUser (payload: any) {
    const { user_uuid } = payload
    const response = await Container.get(SecurityService).getUser(user_uuid)
    this.context.commit('SET_USER', response.data)
    return response
  }

  @Action({ rawError: true })
  async updateCurrentUser (payload: any) {
    const response = await Container.get(SecurityService).updateCurrentUser(payload)
    this.context.dispatch('getCurrentUser')
    return response
  }

  @Action({ rawError: true })
  async updateUser (payload: any) {
    const { user_uuid, data } = payload
    if (data?.profile_photo && data.profile_photo instanceof File) {
      const profile_photo = await Container.get(FileUploadService).uploadFile(data.profile_photo)
      data.profile_photo = profile_photo
    }
    await Container.get(SecurityService).updateUser(user_uuid, data)
    return await this.context.dispatch('getUser', payload)
  }

  @Action({ rawError: true })
  async updateUserNewActivation (payload: any) {
    const { user_uuid, data } = payload
    await Container.get(SecurityService).updateUserNewActivation(user_uuid, data)
    return await this.context.dispatch('getUser', payload)
  }

  @Action({ rawError: true })
  async get2faCode (payload: any) {
    const { data } = payload
    const response = await Container.get(SecurityService).get2faCode(data)
    this.context.commit('SET_TWO_FACTOR_SENT', true)
    return response
  }

  @Action({ rawError: true })
  async send2FAToken (two_factor_purpose: string) {
    return await Container.get(SecurityService).send2FAToken(two_factor_purpose)
  }

  @Action({ rawError: true })
  async verify2FACode (payload: any) {
    return await Container.get(SecurityService).verify2FAToken(payload)
  }

  @Action({ rawError: true })
  async addEventAccess (payload: any) {
    const { event_uuid, user_uuid } = payload
    const response = await Container.get(SecurityService).addEventAccess(event_uuid, user_uuid)
    return response
  }

  @Action({ rawError: true })
  async removeEventAccess (payload: any) {
    const { event_uuid, user_uuid } = payload
    const response = await Container.get(SecurityService).removeEventAccess(event_uuid, user_uuid)
    return response
  }

  @Action({ rawError: true })
  async addEventAccessBulk (payload: any) {
    const { user_uuid, add_events } = payload
    const results = await Container.get(SecurityService).addEventAccessBulk(user_uuid, add_events)
    return results
  }

  @Action({ rawError: true })
  async removeEventAccessBulk (payload: any) {
    const { user_uuid, remove_events } = payload
    const results = await Container.get(SecurityService).removeEventAccessBulk(user_uuid, remove_events)
    return results
  }

  @Action({ rawError: true })
  async getStaxBillPaymentMethods (): Promise<any> {
    try {
      const response = await Container.get(StaxBillService).getPaymentMethods()
      this.context.commit('SET_PAYMENT_METHODS', response.data)
      return response.data
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async createStaxBillCustomer (): Promise<any> {
    try {
      const response = await Container.get(StaxBillService).createCustomer()
      this.context.commit('SET_CURRENT_USER', response.data)
      return response.data
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async verifyStaxBillPaymentMethod ({ id: paymentMethodId, maskedCardNumber, originalId }): Promise<any> {
    try {
      const response = await Container.get(StaxBillService).verifyPaymentMethod(paymentMethodId, maskedCardNumber, originalId)
      if (response && response.data) {
        this.context.commit('UPDATE_PAYMENT_METHOD', { newCard: response.data, originalId })
      }
      return response.data
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async activateStaxBillSubscription (): Promise<any> {
    try {
      return await Container.get(StaxBillService).activateCustomer()
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async deleteCreditCard ({ uuid }): Promise<any> {
    try {
      const response = await Container.get(StaxBillService).deletePaymentMethod(uuid)
      if (response.status === 204) {
        this.context.commit('SET_PAYMENT_METHODS', this.paymentMethods.filter((pm) => pm.uuid !== uuid))
        return true
      }
      return true
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async editCreditCard ({ creditCardData }): Promise<any> {
    try {
      return await Container.get(StaxBillService).editStaxBillCard(creditCardData)
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async makeCreditCardDefault ({ uuid }): Promise<any> {
    try {
      // return await Container.get(StaxBillService).deletePaymentMethod(uuid)
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }
}
